import { useCallback, useEffect, useState } from 'react';
import { Award, Flame } from 'lucide-react';
import { toast } from 'sonner';
import PortalHeader from '../components/PortalHeader';
import { rewardsAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { btnPrimary, EmptyState, panelCls, StatusBadge } from '../components/ui';

function fmt(x) {
  const n = Number(x || 0);
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function WalletPage() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [reds, setReds] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    rewardsAPI.me().then(({ data }) => setMe(data))
      .catch((err) => toast.error(err.response?.data?.detail || t('Could not load your wallet')));
    rewardsAPI.catalog().then(({ data }) => setItems(data.items)).catch(() => {});
    rewardsAPI.myRedemptions().then(({ data }) => setReds(data.items)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const redeem = async (item) => {
    if (!window.confirm(`${t('Redeem')} "${item.title}" ${t('for')} ${fmt(item.cost_points)} ${t('points')}?`)) return;
    setBusy(item.id);
    try {
      await rewardsAPI.redeem(item.id);
      toast.success(t('Redeemed'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy('');
    }
  };

  if (me === null) return <div className="p-6 text-sm text-mms-muted">{t('Loading…')}</div>;
  const b = me.balances || {};
  const lvl = me.level || {};

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <PortalHeader title={t('Wallet')} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {['points', 'xp', 'credit', 'cash'].map((c) => (
          <div key={c} className={`${panelCls} p-4`}>
            <div className="text-xs uppercase tracking-wide text-mms-faint">{t(c)}</div>
            <div className="mt-1 text-xl font-semibold mms-num">{fmt(b[c]?.available)}</div>
            {b[c]?.pending ? (
              <div className="mt-0.5 text-xs text-mms-muted mms-num">{t('pending')}: {fmt(b[c].pending)}</div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className={`${panelCls} p-5`}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Award size={16} className="text-mms-accent" />
            {t('Level')} {lvl.n} — {lvl.name}
            {lvl.multiplier ? <span className="text-xs text-mms-muted">(+{Math.round(lvl.multiplier * 100)}% {t('points')})</span> : null}
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-mms-raised">
            <div className="h-full rounded-full bg-mms-accent" style={{ width: `${Math.round((me.progress || 0) * 100)}%` }} />
          </div>
          <div className="mt-1.5 text-xs text-mms-faint mms-num">
            {fmt(me.xp)} XP{me.next ? ` · ${t('next')}: ${me.next.name} ${t('at')} ${fmt(me.next.xp)} XP` : ` · ${t('top level')}`}
          </div>
          {me.badges?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {me.badges.map((bd) => (
                <span key={bd.badge_id} className="rounded-full border border-mms-accent px-2 py-0.5 text-xs text-mms-accent">
                  {bd.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={`${panelCls} p-5`}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Flame size={16} className="text-[var(--mms-warning)]" />
            {t('Login streak')}
          </div>
          <div className="mt-2 text-2xl font-semibold mms-num">{me.streak?.current || 0} <span className="text-sm font-normal text-mms-muted">{t('days')}</span></div>
          <div className="mt-0.5 text-xs text-mms-faint mms-num">{t('best')}: {me.streak?.best || 0}</div>
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold text-mms-muted">{t('Redeem points')}</h2>
        {items.length === 0 ? (
          <EmptyState>{t('The rewards catalog is empty for now.')}</EmptyState>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div key={item.id} className={`${panelCls} flex flex-col p-4`}>
                <div className="text-sm font-semibold">{item.title}</div>
                {item.description ? <p className="mt-1 flex-1 text-xs text-mms-muted">{item.description}</p> : <div className="flex-1" />}
                <div className="mt-3 flex items-center justify-between">
                  <span className="mms-num text-sm text-mms-accent">{fmt(item.cost_points)} {t('points')}</span>
                  <button
                    onClick={() => redeem(item)}
                    disabled={busy === item.id || (b.points?.available ?? 0) < item.cost_points}
                    className={btnPrimary}
                  >
                    {t('Redeem')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {reds.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-mms-muted">{t('My redemptions')}</h2>
          <div className={`${panelCls} divide-y divide-mms-border/60`}>
            {reds.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span>{r.item_title}</span>
                <span className="mms-num text-mms-muted">−{fmt(r.cost_points)}</span>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {me.recent?.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-mms-muted">{t('Recent activity')}</h2>
          <div className={`${panelCls} divide-y divide-mms-border/60`}>
            {me.recent.slice(0, 10).map((e) => (
              <div key={e.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="text-xs text-mms-muted">{(e.created_at || '').slice(0, 10)}</span>
                <span className="flex-1 px-3 truncate">{e.rule_name}</span>
                <span className={`mms-num ${e.amount < 0 ? 'text-[var(--mms-danger)]' : ''}`}>
                  {fmt(e.amount)} {t(e.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
